// ============================================================
// ExpressStaticFiles.jsx
//
// A branded, responsive course content page for the
// "Serving Static Files" lesson.
//
// Author: ProDev Architecture Series
// Features: Middleware configuration, virtual path mapping,
//           and SPA deployment logic.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Avatar,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import FolderIcon            from "@mui/icons-material/Folder";
import ImageIcon             from "@mui/icons-material/Image";
import WebIcon               from "@mui/icons-material/Web";
import TerminalIcon          from "@mui/icons-material/Terminal";
import LightbulbIcon         from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon       from "@mui/icons-material/ContentCopy";
import DoneIcon              from "@mui/icons-material/Done";
import SchoolIcon            from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon         from "@mui/icons-material/ArrowBack";
import CodeIcon              from "@mui/icons-material/Code";
import RocketLaunchIcon      from "@mui/icons-material/RocketLaunch";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map((l) => l.cmd).join("\n");

    const handleCopy = () => {
        navigator.clipboard.writeText(fullText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 2 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Box sx={{ display: "flex", gap: 0.7 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span style={{ fontFamily: "monospace" }}>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1 }}>
                {lines.map(({ cmd, comment }, i) => (
                    <Box key={i}>
                        {comment && <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "rgba(255,255,255,0.3)", mb: 0.2 }}>// {comment}</Typography>}
                        <Typography sx={{ fontFamily: "monospace", fontSize: { xs: 13, md: 14 }, color: BRAND.codeText, whiteSpace: "pre-wrap" }}>{cmd}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function ExpressStaticFiles() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (!showScroll && window.pageYOffset > 400) setShowScroll(true);
            else if (showScroll && window.pageYOffset <= 400) setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, [showScroll]);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Assets · Module 20"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Serving <span>Static Files</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Express makes it effortless to serve static assets like images, CSS, and client-side JavaScript. Using the built-in static middleware, you can host entire frontend applications directly from your Node.js server.
                    </Typography>
                </Box>

                {/* 1. BASIC SETUP */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<TerminalIcon />} label="1. Basic Middleware Usage" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        The <code style={{color: BRAND.white}}>express.static()</code> function identifies a directory from which to serve files. Usually, this is named <strong>public</strong> or <strong>dist</strong>.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "const express = require('express');", comment: "" },
                        { cmd: "const app = express();", comment: "" },
                        { cmd: "app.use(express.static('public'));", comment: "Serves files from 'public' folder" },
                        { cmd: "", comment: "" },
                        { cmd: "app.listen(3000);", comment: "Visit http://localhost:3000/image.png" },
                    ]} />
                </Box>

                {/* 2. VIRTUAL PATHS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<WebIcon />} label="2. Virtual Path Prefixes" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        You can create a "virtual" path prefix (which does not exist in the file system) to organize how assets are accessed in the browser.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "app.use('/static', express.static('public'));", comment: "Mount at /static" },
                        { cmd: "", comment: "Access via: http://localhost:3000/static/styles.css" },
                    ]} />
                </Box>

                {/* 3. SPA SUPPORT */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<RocketLaunchIcon />} label="3. Serving SPAs (React/Vue)" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        For Single Page Applications, you serve the build folder and ensure that any unknown routes return the <code style={{color: BRAND.white}}>index.html</code> so the frontend router can take over.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "app.use(express.static('client/build'));", comment: "Serve React build" },
                        { cmd: "", comment: "" },
                        { cmd: "app.get('*', (req, res) => {", comment: "Catch-all route" },
                        { cmd: "  res.sendFile(__dirname + '/client/build/index.html');", comment: "" },
                        { cmd: "});", comment: "" },
                    ]} />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* FILE STRUCTURE CARD */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<FolderIcon />} label="Recommended Structure" />
                    <Paper sx={{ p: 3, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2 }}>
                        <Typography sx={{ fontFamily: 'monospace', color: BRAND.gold, fontSize: 14 }}>
                            project-root/<br/>
                            ├── public/      <span style={{color: BRAND.muted}}># Static assets</span><br/>
                            │   ├── images/  <span style={{color: BRAND.muted}}># logo.png</span><br/>
                            │   ├── css/     <span style={{color: BRAND.muted}}># main.css</span><br/>
                            │   └── js/      <span style={{color: BRAND.muted}}># client.js</span><br/>
                            ├── server.js    <span style={{color: BRAND.muted}}># Express app</span><br/>
                            └── package.json
                        </Typography>
                    </Paper>
                </Box>

                {/* MODULE OUTCOMES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="Learning Outcomes" />
                    <Grid container spacing={1.5}>
                        {[
                            "Configure Express to serve images, CSS, and JS files",
                            "Implement virtual path prefixes for asset organization",
                            "Deploy a production-ready React or Vue build folder",
                            "Understand the difference between absolute and relative paths",
                            "Manage catch-all routes for Single Page Application routing",
                            "Identify best practices for caching and asset delivery",
                        ].map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/express-dashboard"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "999px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": {
                                borderColor: BRAND.goldBright,
                                background: BRAND.goldGlow
                            }
                        }}
                    >
                        Back to Express Dashboard
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> While Express is great for serving files during development, in a high-traffic production environment, it is better to use a dedicated web server like <strong>Nginx</strong> or a <strong>CDN</strong> to serve static assets, leaving Express to handle only API logic.
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
